import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RuleSet } from '@kkp-fraud/shared';
import { ApiError, api, type ApiSettings } from '../api';
import { DEMO } from '../demo';
import { contentHash, fromSchemaRefusal, fromValidation, type CheckResult } from '../ruleCheck';

/**
 * The rule check (Wave 2-0): the engine — `POST /api/rules/validate` — is asked about a
 * document 400 ms after its last edit, or at once from the "Check rules" button.
 *
 *   • A newer edit cancels a check still in flight (AbortController), so an old answer can
 *     never land on a newer draft.
 *   • Every answer remembers the content hash of the document it is about. `current` is true
 *     only while the document on screen still has that hash — and saving / publishing needs
 *     `canSave`: a current answer that says `ok`.
 *   • A 400 `validation_failed` (the engine would not even read the document's shape) is an
 *     answer — "no". A network failure or any other status is not: `status` is `failed` and
 *     `canSave` stays false.
 *   • Demo mode (no server) checks nothing: `status` is `demo` and `canSave` is true — the
 *     release itself is still refused server-side if the document is wrong.
 *   • `enabled: false` (a Viewer, a read-only screen) never calls the server.
 */

export type CheckStatus = 'off' | 'demo' | 'pending' | 'checking' | 'done' | 'failed';

export interface RuleCheck {
  status: CheckStatus;
  /** The latest answer — possibly about an earlier version of the document (see `current`). */
  result: CheckResult | null;
  /** `result` is about the document as it is now. */
  current: boolean;
  /** Save / publish may go ahead: a current answer that is `ok` (or demo mode). */
  canSave: boolean;
  /** Ask now, without waiting for the debounce. */
  checkNow: () => void;
}

/** How long after the last edit the check runs. */
export const CHECK_DELAY_MS = 400;

export interface RuleCheckOptions {
  enabled?: boolean;
  delayMs?: number;
}

export function useRuleCheck(settings: ApiSettings, doc: RuleSet | null, options: RuleCheckOptions = {}): RuleCheck {
  const { enabled = true, delayMs = CHECK_DELAY_MS } = options;
  const hash = useMemo(() => (doc ? contentHash(doc) : ''), [doc]);
  const active = enabled && !DEMO && doc !== null;

  const [result, setResult] = useState<CheckResult | null>(null);
  /** A check running, or one that failed, and the hash it was for. */
  const [phase, setPhase] = useState<{ hash: string; status: 'checking' | 'failed' } | null>(null);

  // The latest values, for the timer and the button — neither should restart the debounce.
  const latest = useRef({ settings, doc, hash });
  latest.current = { settings, doc, hash };
  const resultHash = useRef<string | null>(null);
  resultHash.current = result?.hash ?? null;
  const inFlight = useRef<AbortController | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancel = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
    inFlight.current?.abort();
    inFlight.current = null;
  }, []);

  const run = useCallback(() => {
    const { settings: s, doc: d, hash: h } = latest.current;
    if (!d) return;
    cancel();
    const ctrl = new AbortController();
    inFlight.current = ctrl;
    setPhase({ hash: h, status: 'checking' });
    api.validate(s, d, ctrl.signal).then(
      (v) => {
        if (ctrl.signal.aborted) return;
        inFlight.current = null;
        setResult(fromValidation(h, v));
        setPhase(null);
      },
      (e: unknown) => {
        if (ctrl.signal.aborted) return;
        inFlight.current = null;
        if (e instanceof ApiError && e.status === 400 && e.problems && e.problems.length > 0) {
          setResult(fromSchemaRefusal(h, e.problems));
          setPhase(null);
        } else {
          setPhase({ hash: h, status: 'failed' });
        }
      },
    );
  }, [cancel]);

  useEffect(() => {
    if (!active) {
      cancel();
      return;
    }
    // Already answered for exactly this content (an undo back to a checked draft).
    if (resultHash.current === hash) {
      cancel();
      return;
    }
    // A newer edit: whatever was in flight is about a document that no longer exists.
    cancel();
    timer.current = setTimeout(() => {
      timer.current = null;
      run();
    }, delayMs);
    return () => {
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = null;
    };
  }, [hash, active, delayMs, run, cancel]);

  // Unmounting abandons a check in flight.
  useEffect(() => cancel, [cancel]);

  const checkNow = useCallback(() => {
    if (enabled && !DEMO && latest.current.doc) run();
  }, [enabled, run]);

  const current = result !== null && hash !== '' && result.hash === hash;
  let status: CheckStatus;
  if (DEMO) status = 'demo';
  else if (!enabled || !doc) status = 'off';
  else if (phase && phase.hash === hash) status = phase.status;
  else if (current) status = 'done';
  else status = 'pending';

  return {
    status,
    result,
    current,
    canSave: DEMO || (status === 'done' && result !== null && result.ok),
    checkNow,
  };
}
